import React, { useState, useEffect } from 'react';
import { X, Send, Loader2, CheckCircle, AlertTriangle, Globe, GraduationCap, Plane, Clock } from 'lucide-react';
import { submitContactForm } from '../../services/contactApi';
import vidyazonLogo from '../../img/image3.png';

// ---- DATA: Quotes Definition ----
const QUOTES = [
  { text: "The future depends on what you do today.", author: "Mahatma Gandhi" },
  { text: "Education is the most powerful weapon which you can use to change the world.", author: "Nelson Mandela" },
  { text: "The beautiful thing about learning is that no one can take it away from you.", author: "B.B. King" },
  { text: "Invest in your future. Apply today, succeed tomorrow.", author: "Vidyazon" }
];

function ContactPopup({ isOpen, onClose }) {
  // ---- FORM STATE (Unchanged) ----
  const [formData, setFormData] = useState({
    firstName: '', lastName: '', email: '', phone: '', subject: '', country: '', customCountry: '', message: '', countryCode: '+91',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  // ---- NEW STATE: Clock & Quotes ----
  const [time, setTime] = useState(new Date());
  const [currentQuoteIndex, setCurrentQuoteIndex] = useState(0);
  const [fadeQuote, setFadeQuote] = useState(true);

  // ---- EFFECTS ----
  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : 'unset';
    return () => (document.body.style.overflow = 'unset');
  }, [isOpen]);

  // Real-Time Clock Timer
  useEffect(() => {
    if (!isOpen) return;
    const timerId = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(timerId);
  }, [isOpen]);

  // Rotating Quotes Timer
  useEffect(() => {
    if (!isOpen) return;
    const quoteInterval = setInterval(() => {
      setFadeQuote(false);
      setTimeout(() => {
        setCurrentQuoteIndex((prev) => (prev + 1) % QUOTES.length);
        setFadeQuote(true);
      }, 500);
    }, 5000);
    return () => clearInterval(quoteInterval);
  }, [isOpen]);

  // ---- CLOCK CALCULATION ----
  const seconds = time.getSeconds();
  const minutes = time.getMinutes();
  const hours = time.getHours();

  const secondDegrees = (seconds / 60) * 360;
  const minuteDegrees = ((minutes / 60) * 360) + ((seconds / 60) * 6);
  const hourDegrees = ((hours % 12) / 12 * 360) + ((minutes / 60) * 30);

  // ---- FORM HANDLERS (Unchanged) ----
  const countryCodes = [
    { code: '+1', name: 'USA', flag: '🇺🇸' }, { code: '+44', name: 'UK', flag: '🇬🇧' }, { code: '+61', name: 'Australia', flag: '🇦🇺' }, { code: '+49', name: 'Germany', flag: '🇩🇪' }, { code: '+91', name: 'India', flag: '🇮🇳' }, { code: '+81', name: 'Japan', flag: '🇯🇵' }, { code: '+971', name: 'UAE', flag: '🇦🇪' }, { code: '+33', name: 'France', flag: '🇫🇷' },
  ];
  const countries = [
    {name: 'INDIA', flag: '🇮🇳'}, { name: 'USA', flag: '🇺🇸' }, { name: 'UK', flag: '🇬🇧' }, { name: 'Canada', flag: '🇨🇦' }, { name: 'Australia', flag: '🇦🇺' }, { name: 'Germany', flag: '🇩🇪' }, { name: 'France', flag: '🇫🇷' }, { name: 'Netherlands', flag: '🇳🇱' }, { name: 'Singapore', flag: '🇸🇬' }, { name: 'New Zealand', flag: '🇳🇿' }, { name: 'Ireland', flag: '🇮🇪' }, { name: 'Other', flag: '🌍' }
  ];
  const subjectOptions = ['University Search', 'Application Assistance', 'Scholarship Information', 'Visa Guidance', 'Course Selection', 'General Inquiry'];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true); setError(''); setSuccess(false);
    if (!formData.firstName || !formData.lastName || !formData.email || !formData.phone || !formData.subject || !formData.country || !formData.message) { setError('Please fill in all required fields'); setLoading(false); return; }
    if (formData.country === 'Other' && !formData.customCountry) { setError('Please enter country name'); setLoading(false); return; }
    try {
      const finalCountry = formData.country === 'Other' ? formData.customCountry : formData.country;
      const phoneWithCode = `${formData.countryCode}${formData.phone}`;
      await submitContactForm({ ...formData, phone: phoneWithCode, country: finalCountry });
      setSuccess(true);
      setTimeout(() => {
        setFormData({ firstName: '', lastName: '', email: '', phone: '', subject: '', country: '', customCountry: '', message: '', countryCode: '+91' });
        setSuccess(false); onClose();
      }, 3000);
    } catch (err) { setError(err.message || 'Failed to send message.'); } finally { setLoading(false); }
  };

  if (!isOpen) return null;

  if (success) {
    return (
      <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/70 backdrop-blur-md animate-fadeIn">
        <div className="relative w-full max-w-sm bg-white rounded-3xl shadow-2xl p-8 text-center animate-slideUp">
          <div className="w-20 h-20 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-4 shadow-inner">
             <CheckCircle className="w-10 h-10 text-green-500 animate-bounceDelayed" />
          </div>
          <h2 className="text-2xl font-black text-gray-900 mb-2">Message Sent!</h2>
          <p className="text-gray-500 mb-6 text-sm">We will connect with you shortly.</p>
          <button onClick={onClose} className="w-full py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-bold rounded-xl hover:shadow-lg transition-all">Close</button>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-[#0f172a]/80 backdrop-blur-sm overflow-hidden animate-fadeIn"
      onClick={onClose}
    >
      {/* RESPONSIVE CONTAINER CHANGES:
         1. w-[95%] on mobile -> ensures it doesn't touch edges.
         2. h-[85vh] or max-h-[90vh] -> ensures it fits on screen but allows scrolling.
         3. overflow-hidden -> Keeps border radius clean.
      */}
      <div 
        className="relative w-[95%] max-w-4xl max-h-[90vh] flex flex-col md:flex-row rounded-2xl md:rounded-[2rem] shadow-2xl animate-slideUp bg-white overflow-hidden border border-white/10"
        onClick={(e) => e.stopPropagation()}
      >
        {/* ================= LEFT SIDEBAR (Top on Mobile) ================= */}
        {/* shrink-0 prevents this from collapsing. p-4 on mobile reduces height usage. */}
        <div className="w-full md:w-5/12 bg-gradient-to-br from-[#1e1b4b] via-[#312e81] to-[#4338ca] relative overflow-hidden flex flex-col items-center justify-center md:justify-between p-4 md:p-8 text-white text-center shrink-0">
          
          {/* Background Effects */}
          <div className="absolute top-0 left-0 w-full h-full bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10 z-0"></div>
          <div className="absolute -top-20 -left-20 w-60 h-60 bg-blue-500/20 rounded-full blur-[100px] mix-blend-overlay"></div>
          <div className="absolute bottom-0 right-0 w-60 h-60 bg-purple-500/20 rounded-full blur-[100px] mix-blend-overlay"></div>
          
          {/* Top Branding - Only visible on slightly larger mobile screens (xs) to save space */}
          <div className="relative z-10 mb-2 md:mb-6 hidden xs:block">
              <h1 className="text-lg md:text-2xl font-black tracking-tight text-transparent bg-clip-text bg-gradient-to-r from-blue-200 via-white to-purple-200 filter drop-shadow-sm">VIDYAZON</h1>
              <p className="text-[10px] md:text-xs text-blue-200/80 tracking-widest uppercase">Global Education Pathways</p>
          </div>
          
          {/* Content Wrapper */}
          <div className="relative z-10 flex flex-row md:flex-col items-center gap-4 md:gap-8 justify-center w-full">
            
            {/* ==== REAL-TIME BRANDED CLOCK ==== */}
            {/* Reduced size slightly on mobile (w-20 h-20) to save vertical space */}
            <div className="relative w-20 h-20 md:w-36 md:h-36 rounded-full border-4 border-white/10 shadow-[0_0_30px_rgba(59,130,246,0.3),inset_0_0_20px_rgba(0,0,0,0.2)] bg-gradient-to-br from-white/10 to-transparent backdrop-blur-md shrink-0">
                
                {[...Array(12)].map((_, i) => (
                  <div key={i} className="absolute top-0 left-1/2 w-0.5 h-1.5 md:h-2 bg-white/40 -translate-x-1/2 origin-[50%_40px] md:origin-[50%_72px]" style={{ transform: `rotate(${i * 30}deg)` }}></div>
                ))}

                {/* Center Hub */}
      <div className="absolute top-1/2 left-1/2 w-10 h-10 md:w-16 md:h-16 bg-white rounded-full -translate-x-1/2 -translate-y-1/2 z-30 shadow-[0_4px_10px_rgba(0,0,0,0.3)] flex items-center justify-center border-2 border-blue-400/30 overflow-hidden">
    <img 
        src={vidyazonLogo} 
        alt="Vidyazon" 
        className="w-[70%] h-[70%] object-contain"
    />
</div>


<div className="absolute top-1/2 left-1/2 w-1.5 h-1.5 md:w-2 md:h-2 bg-blue-500 rounded-full -translate-x-1/2 -translate-y-1/2 z-40 ring-2 ring-white/50"></div>




                {/* Center Pin */}
                <div className="absolute top-1/2 left-1/2 w-1.5 h-1.5 md:w-2 md:h-2 bg-blue-500 rounded-full -translate-x-1/2 -translate-y-1/2 z-40 ring-2 ring-white/50"></div>

                {/* Hour Hand */}
                <div 
  className="absolute top-[20%] left-[calc(50%-1.5px)] w-[3px] h-[34%] bg-white origin-bottom rounded-full z-20 shadow-[0_2px_5px_rgba(0,0,0,0.35)]"
  style={{ transform: `rotate(${hourDegrees}deg)` }}
></div>

                {/* Minute Hand */}
                <div className="absolute top-[12%] left-[calc(50%-1px)] w-[2px] h-[38%] bg-blue-200 origin-bottom rounded-full z-20 shadow-sm" style={{ transform: `rotate(${minuteDegrees}deg)` }}></div>
                {/* Second Hand */}
                <div className="absolute top-[5%] left-[calc(50%-0.5px)] w-[1px] h-[45%] bg-red-400 origin-bottom rounded-full z-20 shadow-sm" style={{ transform: `rotate(${secondDegrees}deg)` }}>
                    <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 w-1.5 h-4 bg-red-400 rounded-full"></div>
                </div>
            </div>

            {/* ==== ROTATING QUOTES ==== */}
            <div className={`space-y-1 md:space-y-3 min-h-[50px] md:min-h-[80px] transition-opacity duration-500 ${fadeQuote ? 'opacity-100' : 'opacity-0'} flex flex-col justify-center`}>
              <h3 className="text-xs md:text-lg font-bold leading-tight text-white drop-shadow-lg relative px-2">
                <span className="hidden md:inline absolute -top-4 -left-2 text-4xl text-blue-400/20">“</span>
                {QUOTES[currentQuoteIndex].text}
                <span className="hidden md:inline absolute -bottom-4 -right-1 text-4xl text-blue-400/20 translated-y-4">”</span>
              </h3>
              <div className="flex items-center justify-center gap-2">
                <div className="h-px w-4 md:w-8 bg-blue-400/50"></div>
                <p className="text-[8px] md:text-[10px] text-blue-200 font-semibold uppercase tracking-widest">{QUOTES[currentQuoteIndex].author}</p>
                <div className="h-px w-4 md:w-8 bg-blue-400/50"></div>
              </div>
            </div>

          </div>
           {/* Bottom Features - Hidden on mobile entirely to prioritize Form Space */}
           <div className="hidden md:flex relative z-10 gap-4 text-[10px] font-medium text-blue-200/70 mt-auto pt-6">
               <div className="flex flex-col items-center"><Globe className="w-4 h-4 mb-1 text-blue-400" />Global</div>
               <div className="flex flex-col items-center"><GraduationCap className="w-4 h-4 mb-1 text-purple-400" />Expert</div>
               <div className="flex flex-col items-center"><Plane className="w-4 h-4 mb-1 text-pink-400" />Visa</div>
            </div>
        </div>

        {/* ================= RIGHT SIDE: FORM (Bottom on Mobile) ================= */}
        {/* min-h-0 is crucial here for nested flex scrolling */}
        <div className="flex-1 flex flex-col relative bg-white min-h-0">
          <div className="px-4 py-3 md:px-6 md:py-5 flex justify-between items-start border-b border-gray-100 bg-gray-50/30 shrink-0">
             <div>
                <div className="flex items-center gap-2 text-blue-600 font-semibold mb-1">
                    <Clock className="w-3 h-3 md:w-4 md:h-4" /> <span className="text-[10px] md:text-xs uppercase tracking-wider">It's time to begin</span>
                </div>
                <h2 className="text-lg md:text-2xl font-black text-gray-900">Start Your Journey</h2>
             </div>
             <button onClick={onClose} className="w-8 h-8 md:w-9 md:h-9 flex items-center justify-center rounded-full bg-gray-100 hover:bg-red-100 text-gray-500 hover:text-red-600 transition-all transform hover:rotate-90">
              <X className="w-4 h-4 md:w-5 md:h-5" />
            </button>
          </div>

          {/* Scrollable Form Container - flex-1 allows it to fill remaining space */}
          <div className="flex-1 overflow-y-auto p-4 md:p-6 scrollbar-thin">
            <form onSubmit={handleSubmit} className="space-y-3 md:space-y-4 pb-2">
              <div className="grid grid-cols-2 gap-2 md:gap-3">
                {['firstName', 'lastName'].map((field) => (
                  <div key={field} className="relative">
                    <input type="text" name={field} placeholder={field === 'firstName' ? 'First Name *' : 'Last Name *'} value={formData[field]} onChange={handleChange} className="w-full px-3 py-2.5 md:px-4 md:py-3 rounded-lg md:rounded-xl border-2 border-gray-100 text-xs md:text-sm focus:border-blue-500/50 focus:ring-4 focus:ring-blue-500/10 outline-none transition-all bg-white placeholder-gray-400 font-medium" required />
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-[1.5fr_2fr] gap-2 md:gap-3">
                 <input type="email" name="email" placeholder="Email Address *" value={formData.email} onChange={handleChange} className="w-full px-3 py-2.5 md:px-4 md:py-3 rounded-lg md:rounded-xl border-2 border-gray-100 text-xs md:text-sm focus:border-blue-500/50 focus:ring-4 focus:ring-blue-500/10 outline-none transition-all bg-white placeholder-gray-400 font-medium" required />
                 <div className="flex gap-2">
                    <select name="countryCode" value={formData.countryCode} onChange={handleChange} className="w-20 md:w-24 px-1 md:px-2 py-2.5 md:py-3 rounded-lg md:rounded-xl border-2 border-gray-100 text-xs md:text-sm focus:border-blue-500/50 outline-none bg-white font-medium">
                      {countryCodes.map((c) => (<option key={c.code} value={c.code}>{c.flag} {c.code}</option>))}
                    </select>
                    <input type="tel" name="phone" placeholder="Phone *" value={formData.phone} onChange={handleChange} className="flex-1 px-3 py-2.5 md:px-4 md:py-3 rounded-lg md:rounded-xl border-2 border-gray-100 text-xs md:text-sm focus:border-blue-500/50 focus:ring-4 focus:ring-blue-500/10 outline-none transition-all bg-white placeholder-gray-400 font-medium" required />
                 </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 md:gap-3">
                <select name="subject" value={formData.subject} onChange={handleChange} className="w-full px-3 py-2.5 md:px-4 md:py-3 rounded-lg md:rounded-xl border-2 border-gray-100 text-xs md:text-sm focus:border-blue-500/50 outline-none bg-white text-gray-600 font-medium" required>
                  <option value="">Select Subject *</option> {subjectOptions.map((opt) => (<option key={opt} value={opt}>{opt}</option>))}
                </select>
                <select name="country" value={formData.country} onChange={handleChange} className="w-full px-3 py-2.5 md:px-4 md:py-3 rounded-lg md:rounded-xl border-2 border-gray-100 text-xs md:text-sm focus:border-blue-500/50 outline-none bg-white text-gray-600 font-medium" required>
                  <option value="">Preferred Country *</option> {countries.map((c) => (<option key={c.name} value={c.name}>{c.flag} {c.name}</option>))}
                </select>
              </div>
              {formData.country === 'Other' && (
                <input type="text" name="customCountry" placeholder="Enter Country Name *" value={formData.customCountry} onChange={handleChange} className="w-full px-3 py-2.5 md:px-4 md:py-3 rounded-lg md:rounded-xl border-2 border-gray-100 text-xs md:text-sm focus:border-blue-500/50 outline-none bg-white placeholder-gray-400 font-medium" required />
              )}
              <textarea name="message" rows="3" placeholder="Tell us about your academic goals... *" value={formData.message} onChange={handleChange} className="w-full px-3 py-2.5 md:px-4 md:py-3 rounded-lg md:rounded-xl border-2 border-gray-100 text-xs md:text-sm resize-none focus:border-blue-500/50 focus:ring-4 focus:ring-blue-500/10 outline-none bg-white placeholder-gray-400 font-medium" required></textarea>
              {error && (
                <div className="flex items-center gap-2 p-2 md:p-3 rounded-lg md:rounded-xl bg-red-50/80 border border-red-100 text-red-600 text-xs font-medium animate-shake">
                  <AlertTriangle className="w-3 h-3 md:w-4 md:h-4 flex-shrink-0" /> {error}
                </div>
              )}
              <button type="submit" disabled={loading} className="w-full py-3 md:py-4 rounded-xl text-white font-bold text-sm md:text-base bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 shadow-lg shadow-blue-500/25 transform hover:-translate-y-0.5 transition-all flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed">
                {loading ? <><Loader2 className="w-4 h-4 md:w-5 md:h-5 animate-spin" /><span>Submitting...</span></> : <><Send className="w-4 h-4 md:w-5 md:h-5" /><span>Submit Inquiry</span></>}
              </button>
            </form>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes slideUp { from { opacity: 0; transform: translateY(40px) scale(0.95); } to { opacity: 1; transform: translateY(0) scale(1); } }
        @keyframes shake { 0%, 100% { transform: translateX(0); } 10%, 30%, 50%, 70%, 90% { transform: translateX(-2px); } 20%, 40%, 60%, 80% { transform: translateX(2px); } }
        @keyframes bounceDelayed { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-5px); } }
        .animate-fadeIn { animation: fadeIn 0.3s ease-out; }
        .animate-slideUp { animation: slideUp 0.5s cubic-bezier(0.16, 1, 0.3, 1); }
        .animate-shake { animation: shake 0.5s ease-in-out; }
        .animate-bounceDelayed { animation: bounceDelayed 2s ease-in-out infinite; animation-delay: 0.5s; }
        .scrollbar-thin::-webkit-scrollbar { width: 5px; }
        .scrollbar-thin::-webkit-scrollbar-track { background: transparent; }
        .scrollbar-thin::-webkit-scrollbar-thumb { background: #e2e8f0; border-radius: 10px; }
        .scrollbar-thin::-webkit-scrollbar-thumb:hover { background: #cbd5e1; }
      `}</style>
    </div>
  );
}

export default function App() {
  const [isPopupOpen, setIsPopupOpen] = useState(true);
  
  return (
     <div className="">
        <ContactPopup isOpen={isPopupOpen} onClose={() => setIsPopupOpen(false)} />
     </div>
  );
}